// Seeded deterministic shuffle — content-serving variety.
//
// Item selection used to serve a skill's authored pool in raw JSON order
// (pullItems → withBoundedRepetition round 0 = first N in order), so a
// returning kid — and two different kids at the same skill — saw the SAME
// items in the SAME order. This util produces a deterministic permutation
// from a string seed so the serving order can vary per (kid, skill, day)
// while staying STABLE within a session (a fixed seed → a fixed order, so no
// mid-session reshuffle on a React-Query refetch) and reproducible in tests.
//
// Pure + dependency-free. The PRNG is mulberry32 over a 32-bit FNV-1a hash of
// the seed string — fine for content variety (NOT for anything security- or
// fairness-sensitive).

/** 32-bit FNV-1a hash of a string → uint32. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // h *= 16777619, kept in uint32 via Math.imul.
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 PRNG: seed (uint32) → function returning floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Return a NEW array that is a deterministic permutation of `arr`, keyed by
 *  `seed`. Fisher-Yates with a seeded PRNG. Same seed + same input → same
 *  output (every element preserved exactly once). Does not mutate `arr`. */
export function seededShuffle<T>(arr: readonly T[], seed: string): T[] {
  const out = arr.slice();
  const rand = mulberry32(hashString(seed));
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

/** The local-day bucket (days since epoch, device-local). The serving order
 *  rotates when this changes, so a kid meets the same skill's items in a
 *  fresh order each day without any persistence. */
export function localDayEpoch(now: Date = new Date()): number {
  // Offset by the local timezone so the bucket flips at local midnight.
  const ms = now.getTime() - now.getTimezoneOffset() * 60_000;
  return Math.floor(ms / 86_400_000);
}
